import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { StepNodeView } from './step-node-view'
import { nanoid } from 'nanoid'
import { IconCategory, IconType, type StepDTO } from '@deepguide-ai/types'
import {toBase64} from '@jsonjoy.com/base64';
import { encodeBase64 } from '@/utils/base64'

export interface StepAttributes {
  id?: string
  number?: number
  title?: string
  guideAction?: string
  showTitle?: boolean
  showCaption?: boolean // Show caption below screenshot (for PDF export)
  icon?: string
  iconType?: IconType
  iconCategory?: IconCategory
  iconColor?: string
  showIcon?: boolean
  cropStyle?: 'cropped' | 'full' | 'none'
  // Screenshot attributes
  screenshotUrl?: string
  pageUrl?: string
  screenshotScope?: StepDTO['screenshotScope']
  screenshotWidth?: number
  screenshotHeight?: number
  screenshotZoomLevel?: number
  align?: 'left' | 'center' | 'right'
  // Pan attributes
  screenshotPanX?: number
  screenshotPanY?: number
  // Interaction data
  interactionPoints?: any[]
  elementBox?: any
}


export const StepSpec = {
  group: 'block',
  content: 'block*',
  attrs: {
    id: { default: nanoid(10) },
    title: { default: null },
    showTitle: { default: false },
    showCaption: { default: false }, // Show caption below screenshot
    icon: { default: null },
    iconType: { default: null },
    iconCategory: { default: null },
    iconColor: { default: null },
    showIcon: { default: false },
    // Screenshot attributes
    screenshotUrl: { default: null },
    pageUrl: { default: null },
    screenshotScope: { default: null },
    screenshotWidth: { default: null },
    screenshotHeight: { default: null },
    screenshotZoomLevel: { default: 1 },
    screenshotPanX: { default: 0 },
    screenshotPanY: { default: 0 },
    align: { default: 'center' },
    cropStyle: { default: 'cropped' },
    // Interaction data
    interactionPoints: { default: null },
    elementBox: { default: null },
  },
  toDOM() { return ['div', { 'data-step': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-step]' }],
};


declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    step: {
      /**
       * Insert a step
       */
      insertStep: (attributes?: StepAttributes) => ReturnType
      /**
       * Update step attributes
       */
      updateStep: (attributes: Partial<StepAttributes>) => ReturnType
    }
  }
}

export const StepExtension = Node.create({
  name: 'Step',

  group: 'block',

  content: 'block+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: element => element.getAttribute('data-dg-id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      number: {
        default: 1,
        parseHTML: element => {
          const num = element.getAttribute('data-step-number')
          return num ? parseInt(num) : 1
        },
        renderHTML: attributes => {
          if (!attributes.number) {
            return {}
          }
          return {
            'data-step-number': attributes.number,
          }
        },
      },
      title: {
        default: '',
        parseHTML: element => element.getAttribute('data-title'),
        renderHTML: attributes => {
          if (!attributes.title) {
            return {}
          }
          return {
            'data-title': attributes.title,
          }
        },
      },
      guideAction: {
        default: '',
        parseHTML: element => element.getAttribute('data-guide-action'),
        renderHTML: attributes => {
          if (!attributes.guideAction) {
            return {}
          }
          return {
            'data-guide-action': attributes.guideAction,
          }
        },
      },
      showTitle: {
        default: false,
        parseHTML: element => element.getAttribute('data-show-title') !== 'false',
        renderHTML: attributes => {
          if (attributes.showTitle === undefined) {
            return {}
          }
          return {
            'data-show-title': attributes.showTitle,
          }
        },
      },
      showCaption: {
        default: false,
        parseHTML: element => element.getAttribute('data-show-caption') !== 'false',
        renderHTML: attributes => {
          if (attributes.showCaption === undefined) {
            return {}
          }
          return {
            'data-show-caption': attributes.showCaption,
          }
        },
      },
      icon: {
        default: 'sparkles',
        parseHTML: element => element.getAttribute('data-icon'),
        renderHTML: attributes => {
          if (!attributes.icon) {
            return {}
          }
          return {
            'data-icon': attributes.icon,
          }
        },
      },
      iconType: {
        default: 'REGULAR',
        parseHTML: element => element.getAttribute('data-icon-type'),
        renderHTML: attributes => {
          if (!attributes.iconType) {
            return {}
          }
          return {
            'data-icon-type': attributes.iconType,
          }
        },
      },
      iconCategory: {
        default: 'LUCIDE',
        parseHTML: element => element.getAttribute('data-icon-category'),
        renderHTML: attributes => {
          if (!attributes.iconCategory) {
            return {}
          }
          return {
            'data-icon-category': attributes.iconCategory,
          }
        },
      },
      iconColor: {
        default: '#3b82f6',
        parseHTML: element => element.getAttribute('data-icon-color'),
        renderHTML: attributes => {
          if (!attributes.iconColor) {
            return {}
          }
          return {
            'data-icon-color': attributes.iconColor,
          }
        },
      },
      showIcon: {
        default: false,
        parseHTML: element => element.getAttribute('data-show-icon') === 'true',
        renderHTML: attributes => {
          if (attributes.showIcon === undefined) {
            return {}
          }
          return {
            'data-show-icon': attributes.showIcon,
          }
        },
      },
      cropStyle: {
        default: 'cropped',
        parseHTML: element => element.getAttribute('data-crop-style') || 'cropped',
        renderHTML: attributes => {
          if (!attributes.cropStyle) {
            return {}
          }
          return {
            'data-crop-style': attributes.cropStyle,
          }
        },
      },
      screenshotUrl: {
        default: null,
        parseHTML: element => element.getAttribute('data-screenshot-url'),
        renderHTML: attributes => {
          if (!attributes.screenshotUrl) {
            return {}
          }
          return {
            'data-screenshot-url': attributes.screenshotUrl,
          }
        },
      },
      pageUrl: {
        default: null,
        parseHTML: element => element.getAttribute('data-page-url'),
        renderHTML: attributes => {
          if (!attributes.pageUrl) {
            return {}
          }
          return {
            'data-page-url': attributes.pageUrl,
          }
        },
      },
      screenshotScope: {
        default: null,
        parseHTML: element => element.getAttribute('data-screenshot-scope'),
        renderHTML: attributes => attributes.screenshotScope ? { 'data-screenshot-scope': attributes.screenshotScope } : {},
      },
      screenshotWidth: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('data-screenshot-width')
          return width ? parseInt(width) : null
        },
        renderHTML: attributes => {
          if (!attributes.screenshotWidth) {
            return {}
          }
          return {
            'data-screenshot-width': attributes.screenshotWidth,
          }
        },
      },
      screenshotHeight: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('data-screenshot-height')
          return height ? parseInt(height) : null
        },
        renderHTML: attributes => {
          if (!attributes.screenshotHeight) {
            return {}
          }
          return {
            'data-screenshot-height': attributes.screenshotHeight,
          }
        },
      },
      screenshotZoomLevel: {
        default: 1,
        parseHTML: element => {
          const zoom = element.getAttribute('data-screenshot-zoom-level')
          return zoom ? parseFloat(zoom) : 1
        },
        renderHTML: attributes => {
          if (!attributes.screenshotZoomLevel || attributes.screenshotZoomLevel === 1) {
            return {}
          }
          return {
            'data-screenshot-zoom-level': attributes.screenshotZoomLevel,
          }
        },
      },
      screenshotPanX: {
        default: 0,
        parseHTML: element => {
          const panX = element.getAttribute('data-screenshot-pan-x')
          return panX ? parseFloat(panX) : 0
        },
        renderHTML: attributes => {
          if (!attributes.screenshotPanX || attributes.screenshotPanX === 0) {
            return {}
          }
          return {
            'data-screenshot-pan-x': attributes.screenshotPanX,
          }
        },
      },
      screenshotPanY: {
        default: 0,
        parseHTML: element => {
          const panY = element.getAttribute('data-screenshot-pan-y')
          return panY ? parseFloat(panY) : 0
        },
        renderHTML: attributes => {
          if (!attributes.screenshotPanY || attributes.screenshotPanY === 0) {
            return {}
          }
          return {
            'data-screenshot-pan-y': attributes.screenshotPanY,
          }
        },
      },
      align: {
        default: 'center',
        parseHTML: element => element.getAttribute('data-align') || 'center',
        renderHTML: attributes => {
          if (!attributes.align || attributes.align === 'center') {
            return {}
          }
          return {
            'data-align': attributes.align,
          }
        },
      },
      interactionPoints: {
        default: null,
        parseHTML: element => {
          const encoded = element.getAttribute('data-interaction-points')
          if (!encoded) return null
          try {
            return JSON.parse(atob(encoded))
          } catch (e) {
            return null
          }
        },
        renderHTML: attributes => {
          if (!attributes.interactionPoints) {
            return {}
          }
          return {
            'data-interaction-points': encodeBase64(JSON.stringify(attributes.interactionPoints)),
          }
        },
      },
      elementBox: {
        default: null,
        parseHTML: element => {
          const encoded = element.getAttribute('data-element-box')
          if (!encoded) return null
          try {
            return JSON.parse(atob(encoded))
          } catch (e) {
            return null
          }
        },
        renderHTML: attributes => {
          if (!attributes.elementBox) {
            return {}
          }
          return {
            'data-element-box': encodeBase64(JSON.stringify(attributes.elementBox)),
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="Step"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'Step' }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(StepNodeView)
  },

  addCommands() {
    return {
      insertStep:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              id: `step-${Date.now()}`,
              title: '',
              showTitle: true,
              icon: 'sparkles',
              iconType: 'REGULAR',
              iconCategory: 'LUCIDE',
              iconColor: '#3b82f6',
              showIcon: false,
              ...attributes,
            },
            content: [
              {
                type: 'paragraph',
                content: [
                  
                ],
              },
            ],
          })
        },
      updateStep:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the step with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of a step
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
}) 